import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { CommonFunctions } from '@shared/common-functions';
import { CancelModalComponent } from '@shared/components/cancel-modal/cancel-modal.component';
import { GroupCancelBookingModalComponent } from '@shared/components/group-cancel-booking-modal/group-cancel-booking-modal.component';
import {
  BOOKING_CANCELLATION_PROMPT,
  CANCEL_BOOKING_CONFIRMATION_MODEL,
    DEFAULT_LOGO,
    MARK_AS_PAID_CONFIRMATION,
  USER_ROLES,
} from '@shared/constant/shared.constant';
import {
  OfflineBookingPaymentAction,
  PaymentStatusForMessage,
} from '@core/models/api-response.model';
import { DialogControlService } from '@shared/services/dialog-control.service';
import { ToastService } from '@shared/services/toast.service';
import { ConfirmationService } from 'primeng/api';
import { autoUnsubWith, Utility } from '@shared/services/utility.abstract';
import { BookingFlowService } from '@shared/components/booking-flow/service/booking-flow.service';
import { Router } from '@angular/router';
import { PayRepayModalComponent } from '@shared/components/pay-repay-modal/pay-repay-modal.component';
import { SuperAdminService } from '@core/services/super-admin.service';
import { ModifyBookingsPopupComponent } from '@shared/components/modify-bookings-popup/modify-bookings-popup.component';
import { SharedInvoicePopupComponent } from '@shared/components/shared-invoice-popup/shared-invoice-popup.component';
import { CancelBookingDriverComponent } from '@modules/driver/components/cancel-booking-driver/cancel-booking-driver.component';
import { CancelBookingType } from '@core/models/api.response.modal';
import { AddParkerAndEquipmentDrawerComponent } from '@shared/components/add-parker-and-equipment-drawer/add-parker-and-equipment-drawer.component';
import { SpotCountUpdateModalComponent } from '@shared/components/spot-count-update-modal/spot-count-update-modal.component';
import { CancelOptionModalComponent } from '@shared/components/cancel-option-modal/cancel-option-modal.component';
import {
  BookingStatus,
  BookingType,
  BookingTypeDetails,
} from '@core/models/booking.model';
import { CommonConfirmationModalComponent } from '@shared/components/common-confirmation-modal/common-confirmation-modal.component';
import { GroupModifyBookingsPopupComponent } from '@shared/components/group-modify-bookings-popup/group-modify-bookings-popup.component';
import { LuxonFormatPipe } from '@shared/custom-date-pipe';
import { RefundPaymentModalComponent } from '@shared/components/booking-flow-v1/components/refund-payment-modal/refund-payment-modal.component';

@Component({
  selector: 'app-user-crm-booking',
  templateUrl: './user-crm-booking.component.html',
  styleUrls: ['./user-crm-booking.component.scss'],
  providers: [LuxonFormatPipe]
})
export class UserCrmBookingComponent implements OnInit{
  readonly destroyRef = inject(DestroyRef);
  @ViewChild('drawerHost', { read: ViewContainerRef })
  drawerHost!: ViewContainerRef;
  @Input() bookingList;
  @Input() bookingType;
  @Input() itemsPerPage;
  @Input() totalRecords;
  @Input() BookingCurrentSkip;
  @Input() activeIndex;
  @Input() role;
  @Input() isGroupUser: boolean = false;
  @Output() pgageChnage = new EventEmitter<any>();
  @Output() viewInVoice = new EventEmitter<any>();
  @Output() sendInvoiceOnMail = new EventEmitter<any>();
  @Output() navigateToBookingDetail = new EventEmitter<{
    id: string;
    data: boolean;
  }>();
  @Output() afterCancelApiCall: EventEmitter<any> = new EventEmitter<any>();
  @Output() afterMarkAsPaidOrCancel: EventEmitter<any> =
    new EventEmitter<any>();
  items: any;
  dummy = DEFAULT_LOGO;
  paymentStatusForMessage = PaymentStatusForMessage;
  offlineBookingPaymentAction = OfflineBookingPaymentAction;
  expandedNodes: { [key: string]: boolean } = {};
  expandedChildren: { [key: string]: boolean } = {};
  expandedSpots: { [key: string]: boolean } = {};
  userRoles = USER_ROLES;
  bookingStatus = BookingStatus;

  constructor(
    private readonly confirmationService: ConfirmationService,
    private readonly toastService: ToastService,
    private readonly dialogControlService: DialogControlService,
    private readonly bookingFlowService: BookingFlowService,
    private readonly router: Router,
    private readonly superAdminService: SuperAdminService
  ) {}

  getAssignedSpotsDisplay(node: any): string {
    const spots = this.getAssignedSpotsArray(node);
    return spots.length ? spots.join(', ') : '-';
  }

  getAssignedSpotsPreview(node: any): string {
    const spots = this.getAssignedSpotsArray(node);
    if (spots.length === 0) {
      return '-';
    }
    return spots.slice(0, 3).join(', ');
  }

  getAssignedSpotsOverflowLines(node: any): string[] {
    const spots = this.getAssignedSpotsArray(node);
    if (spots.length <= 3) {
      return [];
    }

    const lines: string[] = [];
    for (let index = 3; index < spots.length; index += 3) {
      lines.push(spots.slice(index, index + 3).join(', '));
    }
    return lines;
  }

  hasSpotOverflow(node: any): boolean {
    return this.getAssignedSpotsArray(node).length > 3;
  }

  isSpotsExpanded(node: any): boolean {
    return !!this.expandedSpots?.[node?.id];
  }

  toggleSpots(node: any): void {
    if (!node?.id) {
      return;
    }
    this.expandedSpots[node.id] = !this.expandedSpots[node.id];
  }

  private getAssignedSpotsArray(node: any): string[] {
    const value =
      node?.assignedSpots ??
      node?.slotNames ??
      node?.slotName ??
      node?.parkingSlot ??
      node?.parkingSlots;

    if (!value) {
      return [];
    }

    const normalizeSpot = (spot: string) =>
      spot === 'Any' ? 'Park Anywhere' : spot;

    if (Array.isArray(value)) {
      return value
        .map((item) =>
          typeof item === 'string' ? item : (item?.slotName as string)
        )
        .filter((spot) => !!spot)
        .map((spot) => normalizeSpot(spot));
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((spot) => spot.trim())
        .filter((spot) => !!spot)
        .map((spot) => normalizeSpot(spot));
    }

    return [];
  }

  ngOnInit() {
    this.superAdminService.popupClosed$.subscribe((refresh) => {
      if (refresh) {
        this.afterCancelApiCall.emit({ result: true });
      }
    });
  }

  openModifyPayment(data) {
    this.dialogControlService.openDialog(ModifyBookingsPopupComponent, {
      styleClass: 'modifypayment',
      data: {
        bookingData: data,
      },
    });
  }

  showData(booking: any, event) {
    const isPendingAndNotPaid =
      booking.status === 'Pending' && booking.label !== 'Paid';

    const isCheckCancelForSuperAdmin =
      booking?.paymentStatus ===
        this.paymentStatusForMessage.CheckoutUrlGenrated ||
      booking?.paymentStatus ===
        this.paymentStatusForMessage.InvoiceGenerated ||
      booking?.paymentStatus ===
        this.paymentStatusForMessage.PaymentLinkExpire ||
      booking?.paymentStatus ===
        this.paymentStatusForMessage.PendingWithOffline ||
      booking?.paymentStatus ===
        this.paymentStatusForMessage.RecurringPendingPayment ||
      booking?.paymentStatus === this.paymentStatusForMessage.Failure ||
      booking?.paymentStatus ===
        this.paymentStatusForMessage.RecurringPendingPayment;

    const isCheckPaymentStatus =
      booking?.paymentStatus ===
        this.paymentStatusForMessage.CheckoutUrlGenrated ||
      booking?.paymentStatus ===
        this.paymentStatusForMessage.InvoiceGenerated ||
      booking?.paymentStatus ===
        this.paymentStatusForMessage.PaymentLinkExpire ||
      booking?.paymentStatus ===
        this.paymentStatusForMessage.PendingWithOffline ||
      booking?.paymentStatus ===
        this.paymentStatusForMessage.RecurringPendingPayment;

    const actionMap = {
      payFunc: () => {
        this.dialogControlService.openDialog(PayRepayModalComponent, {
          styleClass: 'pay-repay',
          data: {
            id: booking.id,
            invoiceId:null,
            driverId:this.isGroupUser ? booking.groupUserId: booking.createdBy,
            bookingType:true,
            endDate: booking.endDate,
            timeZone:booking.timeZone,
            bookingScheduledDate:CommonFunctions.getScheduleStatus(booking.bookingScheduledAction,booking.bookingScheduledDate),
            isScheduleOptionShow:true
          },
        });
        this.dialogControlService.onClose().subscribe(() => {
          this.afterMarkAsPaidOrCancel.emit(true);
          this.afterCancelApiCall.emit(true);
        });
      },
      cancel: () => {
        this.cancelBookingModalSuperAdmin(
          booking.id,
          booking.total,
          booking?.usedCredit,
          booking.spotCount,
          booking.bookingPaymentMode
        );
      },
      cancelForSuperAdminCheck: () => {
        if (!isCheckCancelForSuperAdmin) {
          this.confirmationService.confirm({
            target: event.target as EventTarget,
            message: BOOKING_CANCELLATION_PROMPT,
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Cancel Recurring Only',
            rejectLabel: 'Cancel Booking',
            accept: () => {
              setTimeout(() => {
                this.handleCancelRecurring(booking, booking.id, event);
              }, 200);
            },
            reject: () => {
              actionMap.cancelForSuperAdmin();
            },
          });
        } else {
          this.handleCancelRecurring(booking, booking.id, event);
        }
      },
      markAsPaid: (product, event) => {
        this.confirmationService.confirm({
          target: event.target as EventTarget,
          message: MARK_AS_PAID_CONFIRMATION,
          icon: 'pi pi-exclamation-triangle',
          accept: () => {
            this.markAsPaidFunc(product?.id);
          },
        });
      },

      cancelForSuperAdmin: () => {
        if (booking.userType === 'Parker') {
          actionMap.cancelByTwoHours();
          return;
        }
        if (this.activeIndex === 3) {
          actionMap.groupOperatorRecurringCancelBookingBySuperAdmin();
        } else if (this.activeIndex === 0) {
          actionMap.groupOperatorOneTimeCancelBookingBySuperAdmin();
        }
      },
      modifyPayment: () => this.openModifyPayment(booking),
      cancelByTwoHours: () => {
        this.cancelBookingDriverGOModal(
          booking.id,
          booking.total,
          0,
          booking.bookingPaymentMode,
          booking.userType,
          true
        );
      },
      groupOperatorRecurringCancelBookingBySuperAdmin: () => {
        this.cancelBookingModalSuperAdmin(
          booking.id,
          booking.total,
          0,
          booking.spotCount,
          booking.bookingPaymentMode
        );
      },
      cancelRecurring: () =>
        this.handleCancelRecurring(booking, booking.id, event),
      groupOperatorOneTimeCancelBookingBySuperAdmin: () => {
        this.cancelBookingModalSuperAdmin(
          booking.id,
          booking.total,
          0,
          booking.spotCount,
          booking.bookingPaymentMode
        );
      },
      paymentReminder: () => this.sendPaymentReminder(booking.id),
      shareGateAccess: () => this.shareGateAccess(booking.id),
      spotCountUodate: () => {
        this.dialogControlService.openDialog(SpotCountUpdateModalComponent, {
          styleClass: 'spot-count-model',
          data: {
            bookingData: booking,
          },
        });
      },
      voidBooking: () => {
        this.dialogControlService.openDialog(CommonConfirmationModalComponent, {
          styleClass: 'void-booking-model',
          data: {
            headerText: 'Void Booking',
            bodyText: 'Are you sure you want to void <br /> this booking?',
            successIcon: false,
            errorIcon: true,
          },
        });
        this.dialogControlService.onClose().subscribe((confirmed) => {
          if (confirmed) {
            this.voidBookingMethod(booking.id);
          }
        });
      },
      paymentRefund:() =>{
           this.openRefundPaymentModal(booking)
        },
      modifyBookingGo: () => {
        this.dialogControlService.openDialog(
          GroupModifyBookingsPopupComponent,
          {
            styleClass: 'modifypayment',
            data: {
              bookingData: booking,
              showModifyOption: this.isModifyBookingVisible(
                booking,
                booking.userType
              ),
              showSpotCountModify: this.isSoptCountModify(booking),
              showPaymentDateUpdate: this.role === this.userRoles.SUPER_ADMIN && booking.bookingType === 2,
            },
          }
        );
      },
      cancelBookingOption: () =>
        this.cancelOptionModal(
          booking?.bookingType,
          booking?.id,
          booking?.paymentStatus,
          booking?.endDate,
          booking.timeZone,
          booking.bookingScheduledDate,
          booking.bookingScheduledAction
        ),
    };
    this.items = [
      {
        items: [
          {
            label: '',
            icon: '',
          },
          {
            label: 'View Invoice',
            icon: 'common-icon view-profile',
            escape: false,
            command: () => {
              this.viewInVoice.emit(booking?.id);
            },
          },
          {
            label: 'Share Invoice',
            icon: 'common-icon share-invoice',
            escape: false,
            command: () => {
              this.dialogControlService.openDialog(
                SharedInvoicePopupComponent,
                {
                  styleClass: 'shareinvoice-model',
                  data: {
                    bookingId: booking.id,
                    email: booking?.driverEmailId,
                    mobileNumber: booking?.phoneNumber
                      ? booking?.countryCode + ' ' + booking?.phoneNumber
                      : null,
                    isBookingExist: true,
                  },
                }
              );
            },
          },
          {
            label: 'Booking Details',
            icon: 'common-icon reservation-details',
            escape: false,
            command: () => {
              let data = booking?.groupName ? true : false;
              this.navigateToBookingDetail.emit({ id: booking.id, data });
            },
          },
          {
            label: 'Re-Pay',
            icon: 'common-icon custom-pay',
            visible:
              booking.paymentStatus === this.paymentStatusForMessage.Failure || (
              booking.bookingType === BookingTypeDetails.Recurring && (booking?.paymentStatus === this.paymentStatusForMessage.DisputeLoss || booking?.paymentStatus === this.paymentStatusForMessage.DisputeExpired)),
            command: actionMap.payFunc,
          },
          {
            label: 'Pay',
            icon: 'common-icon custom-pay',
            visible:
              isCheckPaymentStatus &&
              !(
                booking?.paymentStatus ===
                this.paymentStatusForMessage.RecurringPendingPayment
              ),
            command: actionMap.payFunc,
          },
          {
            label: 'Mark as Paid',
            icon: 'common-icon mark-as-paid',
            escape: false,
            visible:
              isPendingAndNotPaid &&
              !(
                booking?.paymentStatus ===
                  this.paymentStatusForMessage.Completed ||
                booking?.paymentStatus ===
                  this.paymentStatusForMessage.Processing ||
                booking?.paymentStatus ===
                  this.paymentStatusForMessage.PartialCancel
              ),
            command: () => actionMap.markAsPaid(booking, event),
          },

          {
            label: 'Message',
            icon: 'common-icon message-icon',
            escape: false,
            visible: booking.userType !== 'Group Operator',
            command: () => {
              return CommonFunctions.navigateToMessageRoute(
                booking?.id,
                this.role,
                this.router
              );
            },
          },
          ...(booking?.userType === 'Parker' && booking.bookingType !== 2
            ? [
                {
                  label: 'Modify Booking',
                  icon: 'common-icon modify-icon',
                  visible: this.isModifyBookingVisible(booking, 'Parker'),
                  command: actionMap.modifyPayment,
                },
              ]
            : [
                {
                  label: 'Modify Booking',
                  icon: 'common-icon modify-icon',
                  visible: this.showModifyBookingPopup(booking),
                  command: actionMap.modifyBookingGo,
                },
              ]),
          {
            label: 'Send Payment Reminder',
            icon: 'common-icon send-payment-reminder',
            visible:
              isPendingAndNotPaid &&
              booking.bookingType === BookingTypeDetails.Recurring,
            command: actionMap.paymentReminder,
          },
          {
            label: 'Cancel Booking',
            icon: 'common-icon cancel-recurring',
            visible: this.showCancelBookingModal(booking),
            command: () => actionMap.cancelBookingOption(),
          },
          {
            label: 'Share Open Gate Link',
            icon: 'common-icon share-gate-access',
            visible: this.isShareGateAccessVisible(booking),
            command: actionMap.shareGateAccess,
          },
          {
            label: 'Assign Parkers and Equipment',
            icon: 'common-icon assign-parkers',
            visible:
              booking.userType === 'Group Operator' &&
              CommonFunctions.assignParkerAction(booking),
            command: () => {
              this.openDrawer(
                booking.groupUserId,
                booking.id,
                booking.spotCount,
                this.isShareGateAccessVisible(booking)
              );
            },
          },
          {
            label: 'Void Booking',
            icon: 'common-icon void-booking-icon',
            visible: this.isVoidBookingActionVisible(booking),
            command: actionMap.voidBooking,
          },
          {
            label: 'Payment Refund',
            icon: 'common-icon payment-refund-icon',
            visible: booking?.paymentStatus === this.paymentStatusForMessage.Completed &&
            booking?.refundAmount == 0.00 &&
            booking?.maximumCustomRefundAmount !== 0.00 &&
            this.role === this.userRoles.SUPER_ADMIN && booking?.bookingPaymentMode !== 2,
            command: actionMap.paymentRefund,
          },
        ],
      },
    ];
  }

  showModifyBookingPopup(booking: any) {
    const AllowedIndex = !(
      booking.status === 'Completed' || booking.status === 'Cancelled'
    );
    if (booking.userType === 'Group Operator' || (booking.userType === 'Parker' && booking.bookingType === 2)) {
      return (
        (this.isModifyBookingVisible(booking, booking.userType) ||
          this.isSoptCountModify(booking) ||
          (this.role === this.userRoles.SUPER_ADMIN && booking.bookingType === 2)) &&
        AllowedIndex
      );
    }
    return AllowedIndex;
  }

  getTimeZoneAbbreviation(timeZone: string): string {
    return CommonFunctions.getTimeZoneAbbreviation(timeZone);
  }

  showCancelBookingModal(bookingDetail: any): boolean {
    if (!CommonFunctions.bookingDateIsBetweenStartEndDate(bookingDetail)) {
      return false;
    }
    const { isSubscriptionCancel, isCancelled, status } = bookingDetail || {};
    return !(
      isSubscriptionCancel ||
      isCancelled ||
      ['Completed'].includes(status)
    );
  }
  isSuperAdminAndActiveTab(_booking): boolean {
    return false;
  }

  openRefundPaymentModal(product): void {
    this.dialogControlService.openDialog(RefundPaymentModalComponent, {
      styleClass: 'refund-payment-model',
      data: {
        bookingId: product.id,
        totalPaidAmmount: product?.maximumCustomRefundAmount || product?.total
      },
    });
  }

  cancelRecurringChecks(booking): boolean {
    let checkCancelRecurring: boolean = false;
    if (
      this.activeIndex === 3 &&
      Object.hasOwn(booking, 'children') &&
      !booking.isSubscriptionCancel
    ) {
      switch (booking.paymentStatus) {
        case 7:
          if (
            this.arrivalTimeLessThanTwo(booking.arrivalTimeStamp) < 2 &&
            booking.parentId === null
          ) {
            checkCancelRecurring = true;
          }
          if (booking.status === 'Partially Cancelled') {
            checkCancelRecurring = true;
          }
          break;
        case 4:
        case 9:
          checkCancelRecurring = false;
          break;
        case 2:
          if (this.arrivalTimeLessThanTwo(booking.arrivalTimeStamp) < 2) {
            checkCancelRecurring = true;
          }
          break;
        case 11:
          if (
            booking.status === 'Upcoming' ||
            booking.status === 'In Progress'
          ) {
            checkCancelRecurring = true;
          }
          break;
        default:
          checkCancelRecurring = true;
          break;
      }
    }
    return checkCancelRecurring;
  }

  private handleCancelRecurring(product, bookingId: string, event: Event) {
    event.preventDefault();
    this.cancleRecurringBooking(product, bookingId, event);
  }

  isModifyBookingVisible(product: any, userType: string): boolean {
    const isParkerAndAllowedIndex =
      product?.userType === userType &&
      (product.status === 'Pending' ||
        product.status === 'In Progress' ||
        product.status === 'Partially Cancelled' ||
        product.status === 'Payment Failed' ||
        product.status === 'Upcoming');
    if (product.bookingType === 2) {
      if (this.role === this.userRoles.SUPER_ADMIN) {
        return isParkerAndAllowedIndex;
      }
      return (
        isParkerAndAllowedIndex &&
        product.parentId === null &&
        (!product?.children || product?.children.length === 0) &&
        (!product?.parent?.children || product?.parent?.children.length === 0)
      );
    }
    return isParkerAndAllowedIndex;
  }

  navigateToBookingDetails(booking) {
    let data = booking?.groupName ? true : false;
    this.navigateToBookingDetail.emit({ id: booking.id, data });
  }

  markAsPaidFunc(bookingId: string) {
    let request = {
      bookingId: bookingId,
      timeZone: Utility.getLocalTimeZone(),
    };
    this.bookingFlowService.createBookingPaymentOffline(request).pipe(autoUnsubWith(this.destroyRef)).subscribe({
      next: (_response: any) => {
        this.afterMarkAsPaidOrCancel.emit(true);
        this.updateBookingStateAfterCancellation(_response);
        this.toastService.showToastMessage(
          'success',
          'Success',
          'Booking has been marked as paid.'
        );

      },
      error: (error) => {
        this.toastService.showToastMessage(
          'error',
          'Error',
          error?.error?.message
        );

      },
    });
  }

  sendPaymentReminder(bookingId): void {
    const bookingAndInvoiceId = [bookingId];
    this.sendPaymentReminderApiCall(bookingAndInvoiceId);
  }

  sendPaymentReminderApiCall(bookingAndInvoiceId: string[]): void {
    const requestPayload = {
      bookingAndInvoiceIds: bookingAndInvoiceId,
      timezone: Utility.getLocalTimeZone(),
    };

    this.superAdminService
      .sendPaymentReminder(requestPayload)
      .pipe(autoUnsubWith(this.destroyRef)) // Ensure loader is hidden
      .subscribe({
        next: (response: any) => {
          this.handleReminderSuccess(response);
        },
        error: (error) => {
          this.handleReminderError(error);
        },
      });
  }

  private handleReminderSuccess(response: any): void {
    if (response.message === 'Reminder send successfully') {
      this.toastService.showToastMessage(
        'success',
        'Success',
        'Reminder sent successfully'
      );
    }
  }

  private handleReminderError(error): void {
    this.toastService.showToastMessage('error', 'Error', error.error.message);
  }

 get isAdminOrOwnerRole(): boolean {
    return CommonFunctions.isAdminOrOwnerRole(this.role);
  }

  cancelBookingModalSuperAdmin(
    id: string,
    total: number,
    usedCredit: number,
    spotCount: number,
    bookingPaymentMode: number
  ): void {
    this.dialogControlService.openDialog(GroupCancelBookingModalComponent, {
      data: {
        id: id,
        total: total,
        usedCredit: usedCredit,
        spotCount: spotCount,
        bookingPaymentMode: bookingPaymentMode,
        cancelType: this.activeIndex === 3 ? 'recurring' : 'oneTime',
      },
      styleClass: 'upcoming-cancel',
    });
  }

  arrivalTimeLessThanTwo(inputDate: number): number | null {
    if (this.activeIndex === 0 || this.activeIndex === 3) {
      let currentDate = new Date();
      const date = new Date(inputDate * 1000);
      const timeDifference =
        (date.getTime() - currentDate.getTime()) / (1000 * 60 * 60);
      return timeDifference;
    } else {
      return null;
    }
  }
  openCancelModal(bookindId, bookingPaymentMode: number) {
    this.dialogControlService.openDialog(CancelModalComponent, {
      width: '20%',
      data: {
        id: bookindId,
        headerText: 'Cancel',
        bodyText: CANCEL_BOOKING_CONFIRMATION_MODEL,
        bookingPaymentMode: bookingPaymentMode,
      },
    });
    this.dialogControlService.onClose().subscribe((result) => {
      this.updateBookingStateAfterCancellation(result);
    });
  }

  cancelBookingDriverGOModal(
    id: string,
    total: number,
    usedCredit: number,
    bookingPaymentMode: number,
    userType?: string,
    cancelByAdmin?: boolean
  ): void {
    this.dialogControlService.openDialog(CancelBookingDriverComponent, {
      data: {
        id: id,
        total: total,
        usedCredit: usedCredit,
        role:
          userType === 'Parker'
            ? this.userRoles.DRIVER
            : this.userRoles.GROUP_USER,
        bookingPaymentMode: bookingPaymentMode,
        cancelType: this.activeIndex === 3 ? 'recurring' : 'oneTime',
        cancelByAdmin: cancelByAdmin,
      },
      width: '35%',
    });
    this.dialogControlService.onClose().subscribe((result) => {
      this.afterMarkAsPaidOrCancel.emit(true);
      this.afterCancelApiCall.emit({ result });
    });
  }
  cancleRecurringBooking(_product, bookingId, event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Are you sure you want to cancel the recurring subscription?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {

        let request = {
          bookingId: bookingId,
          slotCount: 0,
          cancelBookingType: CancelBookingType.FullCancelBooking,
          isOnlySubscriptionCancel: true,
        };
        this.superAdminService.cancelRecurringBooking(request).pipe(autoUnsubWith(this.destroyRef)).subscribe({
          next: (response: any) => {
            if (response) {
              this.toastService.showToastMessage(
                'success',
                'Success',
                'Recurring booking cancelled successfully!'
              );
              this.afterMarkAsPaidOrCancel.emit(true);
              this.afterCancelApiCall.emit(true);

            }
          },
          error: (error: HttpErrorResponse) => {
            this.toastService.showToastMessage(
              'error',
              'Error',
              error.error.message
            );

          },
        });
      },
    });
  }

  updateBookingStateAfterCancellation(result): void {
    this.afterCancelApiCall.emit(result);
  }

  showCancelRecurringIcon(product) {
    return (
      !product.isSubscriptionCancel &&
      (product.bookingType > 0 || product.parentId != null) &&
      this.activeIndex !== 2
    );
  }

  onPageChange(event) {
    this.pgageChnage.emit(event);
  }

  onNodeExpand(event: any) {
    const node = event.node;
    node.loading = true;
    const parentId = node.id;
    const bookingType = node.bookingType;
    const request = {
      bookingId: parentId,
      bookingType: bookingType,
      pageNumber: 1,
      pageSize: 10,
      status: BookingType.All
    };

    this.superAdminService.getBookingListWithChildId(request).subscribe({
      next: (res: any) => {
        const mappedChildren = res.map(child => ({
          ...child,
          leaf: true
        }));
        node.children = mappedChildren;
        node.loading = false;
        this.bookingList = [...this.bookingList];
        this.expandedNodes[parentId] = true;
        this.expandChildren(event.node.children);
      },
      error: () => {
        node.loading = false;
      }
    });
  }

  onNodeCollapse(event: any) {
    const nodeId = event.node.id;
    if (nodeId) {
      this.expandedNodes[nodeId] = false;
      this.collapseChildren(event.node.children);
    }
  }

  expandChildren(children: any[]) {
    if (!children) return;
    for (let child of children) {
      this.expandedChildren[child.id] = true;
      this.expandChildren(child.children);
    }
  }

  collapseChildren(children: any[]) {
    if (!children) return;
    for (let child of children) {
      this.expandedChildren[child.id] = false;
      this.collapseChildren(child.children);
    }
  }

  isExpanded(node: any): boolean {
    return !!this.expandedNodes[node.id];
  }

  isChildExpanded(node: any): boolean {
    return !!this.expandedChildren[node.id];
  }

  showActiveRecurring(product) {
    if (!product || product.isSubscriptionCancel) return null;
    const validStatuses = [2, 4, 7, 11];
    const hasValidParentStatus = validStatuses.includes(product.paymentStatus);
    const hasValidChildStatus = product.children?.some((child) =>
      validStatuses.includes(child.paymentStatus)
    );
    if (hasValidParentStatus || hasValidChildStatus) {
      return 'Recurring Active';
    }
    return null;
  }

  displayHyphenForUnpaidOrCancelled(bookingDetails: any): boolean {
    const statuses = [
      this.paymentStatusForMessage.PendingWithOffline,
      this.paymentStatusForMessage.CheckoutUrlGenrated,
      this.paymentStatusForMessage.InvoiceGenerated,
      this.paymentStatusForMessage.PaymentLinkExpire,
      this.paymentStatusForMessage.Pending,
      this.paymentStatusForMessage.PartialCancel,
      this.paymentStatusForMessage.Failure,
      this.paymentStatusForMessage.Processing,
      this.paymentStatusForMessage.Cancelled,
    ];
    return (
      statuses.includes(bookingDetails?.paymentStatus) ||
      (bookingDetails?.paymentStatus ===
        this.paymentStatusForMessage.Completed &&
        bookingDetails?.status === 'Cancelled')
    );
  }

  isShareGateAccessVisible(booking) {
     const currentDate = new Date();
     const currentTimeInSeconds = Math.floor(currentDate.getTime() / 1000);
     let dateCheck = false;

      const tabMap: Record<string, 'current' | 'pending' | 'past'> = {
        'In Progress': 'current',
        'Upcoming': 'current',
        'Pending': 'pending',
        'Payment Failed': 'pending',
        'Completed': 'past',
        'Cancelled': 'past'
      };

      const tab = tabMap[booking.status];
      if (tab) {
        dateCheck = CommonFunctions.openGatePass(tab, booking) === 'hyperlink';
      }

const validStatuses = [
        this.paymentStatusForMessage.Pending,
        this.paymentStatusForMessage.Failure,
        this.paymentStatusForMessage.CheckoutUrlGenrated,
        this.paymentStatusForMessage.InvoiceGenerated,
        this.paymentStatusForMessage.PaymentLinkExpire,
        this.paymentStatusForMessage.PendingWithOffline,
        this.paymentStatusForMessage.RecurringPendingPayment,
        this.paymentStatusForMessage.Completed,
        this.paymentStatusForMessage.DisputeCreated,
        this.paymentStatusForMessage.DisputeUpdated,
        this.paymentStatusForMessage.DisputeLoss,
        this.paymentStatusForMessage.DisputeExpired,
        this.paymentStatusForMessage.Processing,
      ];

      return (
        booking.isOpenGateIntegarted &&
        booking.isCancelled === false &&
        booking.status !== 'Completed' &&
        booking.isLastBillingCycle === true &&
        currentTimeInSeconds <= booking.departureTimeStamp &&
        validStatuses.includes(booking.paymentStatus) &&
        dateCheck
      );
  }

  shareGateAccess(bookingId) {

    let data = {
      bookingId: bookingId,
    };
    this.bookingFlowService.generateAndSendMailForTruxLink(data).pipe(autoUnsubWith(this.destroyRef)).subscribe({
      next: (response: any) => {

        this.toastService.showToastMessage(
          'success',
          'Success',
          response.message
        );
      },
      error: (error) => {
        this.toastService.showToastMessage(
          'error',
          'Error',
          error.error.message
        );

      },
    });
  }

  openDrawer(userId, bookingId, spotCount, isGateAccessLinkVisible) {
    this.drawerHost.clear();
    const componentRef = this.drawerHost.createComponent(
      AddParkerAndEquipmentDrawerComponent
    );
    componentRef.instance.groupUserId = userId;
    componentRef.instance.bookingId = bookingId;
    componentRef.instance.spotCount = spotCount;
    componentRef.instance.isGateAccessLinkVisible = isGateAccessLinkVisible;
  }
  isSoptCountModify(product): boolean {
    if (
      product &&
      product.userType === 'Group Operator' &&
      product.isLastBillingCycle
    ) {
      return (
        (product?.paymentStatus === this.paymentStatusForMessage.CheckoutUrlGenrated ||
          product?.paymentStatus === this.paymentStatusForMessage.InvoiceGenerated ||
          product?.paymentStatus === this.paymentStatusForMessage.PaymentLinkExpire ||
          product?.paymentStatus === this.paymentStatusForMessage.PendingWithOffline ||
          product?.paymentStatus === this.paymentStatusForMessage.RecurringPendingPayment ||
          product?.paymentStatus === this.paymentStatusForMessage.Failure ||
          product?.paymentStatus === this.paymentStatusForMessage.Completed ||
          product?.paymentStatus === this.paymentStatusForMessage.DisputeCreated ||
          product?.paymentStatus === this.paymentStatusForMessage.DisputeUpdated ||
          product?.paymentStatus === this.paymentStatusForMessage.DisputeExpired ||
          product?.paymentStatus === this.paymentStatusForMessage.DisputeLoss) &&
        (product.status === 'Upcoming' ||
          product.status === 'In Progress' ||
          product.status === 'Pending')
      );
    }
    return false;
  }

  cancelOptionModal(bookingType: number, id:string, paymentStatus: number, endDate: string,timeZone:string,bookingScheduledDate:string, bookingScheduledAction:string ) {
    this.dialogControlService.openDialog(CancelOptionModalComponent, {
      styleClass: 'cancel-option-model',
      data: {
        bookingType: bookingType,
        bookingId: id,
        paymentStatus: paymentStatus,
        endDate: endDate,
        timeZone: timeZone,
        bookingScheduledDate:CommonFunctions.getScheduleStatus(bookingScheduledAction,bookingScheduledDate)
      }
    });
  }

  voidBookingMethod(bookingId) {
    this.bookingFlowService.voidBookingById(bookingId).pipe(autoUnsubWith(this.destroyRef)).subscribe({
      next: (response: any) => {

        this.toastService.showToastMessage(
          'success',
          'Success',
          response.message
        );
        this.afterCancelApiCall.emit(true);
      },
      error: (error) => {
        this.toastService.showToastMessage(
          'error',
          'Error',
          error.error.message
        );

      },
    });
  }

  isVoidBookingActionVisible(booking): boolean {
    let showVoidBookingForPendingCase = [2, 4, 11].includes(
      booking.paymentStatus
    );
    return !showVoidBookingForPendingCase

  }
}
